import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  DocumentDraft,
  DocumentDraftContent,
  DocumentDraftModifier,
} from '@/entities/document-draft';
import { type DraftRef } from './documentDraftPersistence';

interface UseDocumentDraftStateProps {
  loadedDraft: DocumentDraft | null;
  currentModifier: DocumentDraftModifier | null;
  onEdit: () => void;
}

export interface UseDocumentDraftStateResult {
  draft: DocumentDraft | null;
  draftOverride: DocumentDraft | null;
  draftRef: DraftRef;
  updateDraft(patch: Partial<DocumentDraftContent>): void;
  updateDocumentMarkdown(documentMarkdown: string): void;
}

export function useDocumentDraftState({
  loadedDraft,
  currentModifier,
  onEdit,
}: UseDocumentDraftStateProps): UseDocumentDraftStateResult {
  const [draftOverride, setDraftOverride] = useState<DocumentDraft | null>(null);
  const draft = useMemo(
    () => (draftOverride === null ? loadedDraft : draftOverride),
    [draftOverride, loadedDraft]
  );
  const draftRef = useRef<DocumentDraft | null>(null);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const updateDraft = useCallback(
    (patch: Partial<DocumentDraftContent>) => {
      const base = draftRef.current;
      if (base === null || currentModifier === null) {
        return;
      }
      const nextDraft: DocumentDraft = {
        ...base,
        content: {
          ...base.content,
          ...patch,
        },
        updatedAt: new Date().toISOString(),
        modifiedBy: currentModifier,
      };
      draftRef.current = nextDraft;
      onEdit();
      setDraftOverride(nextDraft);
    },
    [currentModifier, onEdit]
  );

  const updateDocumentMarkdown = useCallback(
    (documentMarkdown: string) => {
      updateDraft({ bodyMarkdown: documentMarkdown });
    },
    [updateDraft]
  );

  return {
    draft,
    draftOverride,
    draftRef,
    updateDraft,
    updateDocumentMarkdown,
  };
}
